import { mask as turfMask } from "@turf/mask";
import Loader from "components/layouts/loader";
import lodash, { debounce } from "lodash";
import type { MapboxGeoJSONFeature, MapMouseEvent } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import {
  FullscreenControl,
  Layer,
  Map as BaseMap,
  MapProvider,
  NavigationControl,
  Popup,
  ScaleControl,
  Source,
  type MapRef,
  type ViewStateChangeEvent,
} from "react-map-gl";
import { classNames } from "utils";
import {
  buildDistrictsGeoJson,
  findDistrictById,
  fitDistrictsBounds,
  toBoundaryFeature,
  type District,
  type DistrictFeatureProperties,
} from "./utils";

interface DistrictsMapProps {
  districts?: District[];
  loading?: boolean;
  selectedDistrictId?: string;
  regionId?: string;
  onDistrictSelect: (districtId: string) => void;
}

const DEFAULT_LONGITUDE = -0.234361;
const DEFAULT_LATITUDE = 5.667032;

const availableMapStyles = {
  default: "mapbox://styles/mapbox/streets-v12",
  satellite: "mapbox://styles/mapbox/satellite-v9",
} as const;

type MapStyleKey = keyof typeof availableMapStyles;

const mapStyleLabels: Record<MapStyleKey, string> = {
  default: "Streets",
  satellite: "Satellite",
};

const DISTRICTS_SOURCE_ID = "district-boundaries";
const DISTRICTS_FILL_LAYER_ID = "districts-fill";
const DISTRICTS_LINE_LAYER_ID = "districts-line";

interface HoveredDistrict extends DistrictFeatureProperties {
  longitude: number;
  latitude: number;
}

export const DistrictsMap: FC<DistrictsMapProps> = ({
  districts = [],
  loading = false,
  selectedDistrictId,
  regionId,
  onDistrictSelect,
}) => {
  const mapRef = useRef<MapRef>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapStyle, setMapStyle] = useState<MapStyleKey>("default");
  const [hoveredDistrict, setHoveredDistrict] = useState<HoveredDistrict | null>(
    null,
  );
  const [viewState, setViewState] = useState({
    longitude: DEFAULT_LONGITUDE,
    latitude: DEFAULT_LATITUDE,
    zoom: 9,
  });

  const districtsGeoJson = useMemo(
    () => buildDistrictsGeoJson(districts),
    [districts],
  );

  const selectedDistrict = useMemo(
    () => findDistrictById(districts, selectedDistrictId),
    [districts, selectedDistrictId],
  );

  const boundaryLayers = useMemo(() => {
    const outline = toBoundaryFeature(selectedDistrict?.geoBoundary);
    if (!outline) return null;

    return {
      mask: turfMask(outline),
      outline,
    };
  }, [selectedDistrict]);

  const debouncedOnMove = useMemo(
    () => debounce((evt: ViewStateChangeEvent) => setViewState(evt.viewState), 1),
    [],
  );

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();
    const fitMap = () => {
      fitDistrictsBounds(map, districts, {
        region: regionId,
        district: selectedDistrictId,
      });
    };

    if (map.loaded()) {
      fitMap();
    } else {
      map.once("load", fitMap);
    }
  }, [districts, regionId, selectedDistrictId]);

  const handleMouseMove = useCallback((event: MapMouseEvent) => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const feature = event.features?.find(
      (item: MapboxGeoJSONFeature) =>
        item.layer?.id === DISTRICTS_FILL_LAYER_ID ||
        item.layer?.id === DISTRICTS_LINE_LAYER_ID,
    );

    if (!feature?.properties) {
      setHoveredDistrict(null);
      map.getCanvas().style.cursor = "";
      return;
    }

    const properties = feature.properties as DistrictFeatureProperties;
    map.getCanvas().style.cursor = "pointer";
    setHoveredDistrict({
      _id: properties._id,
      name: properties.name,
      code: properties.code,
      regionName: properties.regionName,
      regionCode: properties.regionCode,
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) {
      map.getCanvas().style.cursor = "";
    }
    setHoveredDistrict(null);
  }, []);

  const handleClick = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.find(
        (item: MapboxGeoJSONFeature) =>
          item.layer?.id === DISTRICTS_FILL_LAYER_ID ||
          item.layer?.id === DISTRICTS_LINE_LAYER_ID,
      );

      if (feature?.properties?._id) {
        event.originalEvent.stopPropagation();
        onDistrictSelect(String(feature.properties._id));
        return;
      }

      onDistrictSelect("");
    },
    [onDistrictSelect],
  );

  return (
    <div className="flex-1 p-6 min-h-0">
      <div
        id="districts-map-container"
        className="w-full h-full relative rounded-md overflow-hidden"
      >
        <MapProvider>
          <BaseMap
            ref={mapRef}
            id="districts-map"
            initialViewState={{
              longitude: DEFAULT_LONGITUDE,
              latitude: DEFAULT_LATITUDE,
              zoom: 9,
            }}
            viewState={viewState as any}
            mapStyle={availableMapStyles[mapStyle]}
            mapboxAccessToken={"pk.eyJ1IjoicG9seW1vcnBobGFicyIsImEiOiJjbHU2czRmMDExdWF4MmlrMWEyd2F6eGh6In0.yxIBzfbNIDLTYMDGmAbPiA"}
            onMove={debouncedOnMove}
            onLoad={() => setMapLoaded(true)}
            onStyleData={() => setMapLoaded(true)}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
            interactiveLayerIds={
              mapLoaded ? [DISTRICTS_FILL_LAYER_ID, DISTRICTS_LINE_LAYER_ID] : []
            }
            style={{ width: "100%", height: "100%" }}
          >
            <NavigationControl position="top-right" />
            <ScaleControl position="bottom-right" />
            <FullscreenControl
              position="top-right"
              containerId="districts-map-container"
            />

            {mapLoaded && districtsGeoJson.features.length > 0 && (
              <Source
                id={DISTRICTS_SOURCE_ID}
                type="geojson"
                data={districtsGeoJson}
              >
                <Layer
                  id={DISTRICTS_FILL_LAYER_ID}
                  source={DISTRICTS_SOURCE_ID}
                  type="fill"
                  paint={{
                    "fill-color": [
                      "case",
                      ["==", ["get", "_id"], selectedDistrictId ?? ""],
                      "#1da1f2",
                      "#1da1f2",
                    ],
                    "fill-opacity": [
                      "case",
                      ["==", ["get", "_id"], selectedDistrictId ?? ""],
                      0.15,
                      0.04,
                    ],
                  }}
                />
                <Layer
                  id={DISTRICTS_LINE_LAYER_ID}
                  source={DISTRICTS_SOURCE_ID}
                  type="line"
                  paint={{
                    "line-color": "#f21d1d",
                    "line-width": [
                      "case",
                      ["==", ["get", "_id"], selectedDistrictId ?? ""],
                      3,
                      2,
                    ],
                    "line-opacity": 1,
                    "line-dasharray": [1, 2],
                  }}
                />
              </Source>
            )}

            {mapLoaded && boundaryLayers && (
              <>
                <Source
                  id="district-mask"
                  type="geojson"
                  data={boundaryLayers.mask}
                >
                  <Layer
                    id="district-mask-fill"
                    source="district-mask"
                    type="fill"
                    paint={{
                      "fill-color": "#000000",
                      "fill-opacity": 0.35,
                    }}
                  />
                </Source>
                <Source
                  id="district-outline"
                  type="geojson"
                  data={boundaryLayers.outline}
                >
                  <Layer
                    id="district-selected-fill"
                    source="district-outline"
                    type="fill"
                    paint={{
                      "fill-color": "#1da1f2",
                      "fill-opacity": 0.06,
                    }}
                  />
                  <Layer
                    id="district-selected-line"
                    source="district-outline"
                    type="line"
                    paint={{
                      "line-color": "#f21d1d",
                      "line-width": 3,
                      "line-opacity": 1,
                      "line-dasharray": [1, 2],
                    }}
                  />
                </Source>
              </>
            )}

            {hoveredDistrict && (
              <Popup
                anchor="bottom"
                longitude={hoveredDistrict.longitude}
                latitude={hoveredDistrict.latitude}
                closeButton={false}
                closeOnClick={false}
                className="!font-sans pointer-events-none"
                maxWidth="240px"
              >
                <div className="space-y-1 p-1 font-sans">
                  <p className="text-sm font-semibold text-gray-900">
                    {hoveredDistrict.name ?? "Unknown District"}
                  </p>
                  {hoveredDistrict.code && (
                    <p className="text-xs text-gray-600">
                      Code:{" "}
                      <span className="font-medium text-gray-900">
                        {hoveredDistrict.code}
                      </span>
                    </p>
                  )}
                  {hoveredDistrict.regionName && (
                    <p className="text-xs text-gray-600">
                      Region:{" "}
                      <span className="font-medium text-gray-900">
                        {lodash.startCase(hoveredDistrict.regionName)}
                      </span>
                    </p>
                  )}
                </div>
              </Popup>
            )}
          </BaseMap>
        </MapProvider>

        <div className="absolute bottom-6 left-6 z-10 flex rounded-md shadow-lg overflow-hidden border border-gray-200 bg-white">
          {(Object.keys(availableMapStyles) as MapStyleKey[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setMapStyle(key)}
              className={classNames(
                "px-3 py-1.5 text-xs font-medium transition-colors",
                mapStyle === key
                  ? "bg-primary-600 text-white"
                  : "text-gray-700 hover:bg-gray-50",
              )}
            >
              {mapStyleLabels[key]}
            </button>
          ))}
        </div>

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/25 backdrop-blur-sm">
            <Loader />
          </div>
        )}
      </div>
    </div>
  );
};

export default DistrictsMap;
